import { sleep } from "./sleep"

/**
 * Fetches a data request tand retries a few times
 *
 * @returns REQUEST
 */
export const fetchRetry = async (
  url: string | URL,
  options?: RequestInit,
  n: number = 3,
  delay: number = 300,
  returnRawError: boolean = false,
  timeoutMs: number = 30000
) => {
  for (let i = 0; i < n; i++) {
    try {
      // eslint-disable-next-line no-await-in-loop -- Retry with backoff
      const req = await fetch(url, {
        ...options,
        // A fresh signal per attempt — a shared AbortSignal.timeout would
        // keep counting down across retries and abort attempt 2 early
        signal: options?.signal || AbortSignal.timeout(timeoutMs),
      })

      if (req && req.ok) {
        return req
      }

      // Not Found
      if (req.status === 404) {
        return null
      }

      // Rate-limited — retrying amplifies the problem, fail fast
      if (req.status === 429) {
        if (returnRawError) return req
        throw new Error(`Rate limited (429): ${url}`)
      }

      // On last attempt, return the response even if not ok
      // so fetchApi can read the error details from the response body
      if (i === n - 1) {
        if (returnRawError) {
          return req
        } else {
          throw new Error("Failed to fetch data")
        }
      }

      // eslint-disable-next-line no-await-in-loop -- Retry with backoff
      await sleep(delay * (i + 1))
    } catch (err) {
      // Always propagate 429 — retrying makes rate limiting worse
      if (err instanceof Error && err.message.startsWith("Rate limited")) {
        throw err
      }
      const isLastAttempt = i + 1 === n
      if (isLastAttempt) throw err
    }
  }
}
